// Single source of truth for product-analytics event names and their allowed properties.
// Used by utils/analytics.ts (client fan-out) and by the admin funnel (lib/analytics, admin route).
// Rules: names are stable identifiers (never rename without migrating the funnel definitions);
// props are strings and numbers only, never free text or personal data.

export const EVENTS = {
  wizard_started: 'wizard_started',
  photo_uploaded: 'photo_uploaded',
  faces_confirmed: 'faces_confirmed',
  story_generated: 'story_generated',
  preview_viewed: 'preview_viewed',
  checkout_started: 'checkout_started',
  payment_succeeded: 'payment_succeeded',
  free_cover_requested: 'free_cover_requested',
  free_cover_ready: 'free_cover_ready',
  free_cover_make_book: 'free_cover_make_book',
  share_clicked: 'share_clicked',
  copy_link: 'copy_link',
  share_menu_open: 'share_menu_open',
  merch_checkout_started: 'merch_checkout_started',
  wizard_variant_control: 'wizard_variant_control',
  wizard_variant_fast: 'wizard_variant_fast',
  wizard_step_viewed: 'wizard_step_viewed',
  story_step_viewed: 'story_step_viewed',
  story_requested: 'story_requested',
  cover_preview_requested: 'cover_preview_requested',
  appearance_adjusted: 'appearance_adjusted',
  free_cover_reused: 'free_cover_reused',
  interior_preview_requested: 'interior_preview_requested',
  interior_preview_viewed: 'interior_preview_viewed',
  redo_requested: 'redo_requested',
  // Wizard photo step: what happened between "photo uploaded" and "faces confirmed".
  photo_upload_failed: 'photo_upload_failed',
  faces_detected: 'faces_detected',
  face_detection_failed: 'face_detection_failed',
  // Free-cover steps before the email code is verified (free_cover_requested).
  free_cover_photo_added: 'free_cover_photo_added',
  free_cover_photo_rejected: 'free_cover_photo_rejected',
  free_cover_theme_chosen: 'free_cover_theme_chosen',
  free_cover_code_sent: 'free_cover_code_sent',
  free_cover_code_failed: 'free_cover_code_failed',
  free_cover_verify_failed: 'free_cover_verify_failed',
  free_cover_failed: 'free_cover_failed',
  // Extra copies after a paid print order (thank-you page and the emailed link).
  extra_copies_offer_viewed: 'extra_copies_offer_viewed',
  extra_copies_added: 'extra_copies_added',
  extra_copies_paid: 'extra_copies_paid',
  // Parent-facing create flow (picture-book and comic); every event carries a `flow` prop.
  create_step_viewed: 'create_step_viewed',
  people_confirmed: 'people_confirmed',
  suggestion_kept: 'suggestion_kept',
  suggestion_edited: 'suggestion_edited',
  idea_shuffled: 'idea_shuffled',
  template_picked: 'template_picked',
  // /create-book?story=<id> seeded that story template on Your book; props: template.
  template_preselected: 'template_preselected',
  preview_requested: 'preview_requested',
  preview_ready: 'preview_ready',
  preview_failed: 'preview_failed',
  legal_match_shown: 'legal_match_shown',
  // A parent asked for a new comic cover (same story); props: count = redraws used so far on this book.
  cover_redraw: 'cover_redraw',
  // A parent saved a non-empty comic dedication (no text or length props).
  dedication_saved: 'dedication_saved',
  // The idea names a relative who is not in the photo (Task C3); props: action ('shown' | 'change_photo' | 'continue'),
  // count = how many relatives. The words are never sent (they can be names).
  typed_relative_notice: 'typed_relative_notice',
  // A ready-made story's central person is not in the photo (Task B3: grandparents-day without a grandparent), so
  // Your book shows the notice; props: template, needs (central role ids, e.g. 'grandparent1'). Once per appearance.
  central_role_missing: 'central_role_missing',
  // Public site (P4 redesign). Each click fires exactly one event.
  // A "Who is this book for?" tile; props: recipient, source.
  recipient_tile_clicked: 'recipient_tile_clicked',
  // A story card's cover or name (to /books/<slug>); props: slug, product, source.
  story_card_clicked: 'story_card_clicked',
  // A start-making link; props: variant ('template' | 'free-form'), product?, template?, source.
  create_start_clicked: 'create_start_clicked',
  // The comic-vs-picture-book choice; props: product ('comic' | 'picture-book'), source.
  product_line_clicked: 'product_line_clicked',
  // Resources menu / footer: Blog or Help & FAQ; props: target ('blog' | 'faq'), source ('menu' | 'mobile-menu' | 'footer').
  resources_link_clicked: 'resources_link_clicked',
  // The external dyslexia-screener link; props: source ('menu' | 'mobile-menu' | 'footer').
  screener_outbound_clicked: 'screener_outbound_clicked',
  // The /about link; props: source ('resources' | 'footer' | 'home-founder').
  about_link_clicked: 'about_link_clicked',
  // A /books filter chip; props: filter ('age' | 'theme' | 'for' | 'type'), target (the chip's value id),
  // variant ('on' | 'off': what the click does), source ('books').
  books_filter_clicked: 'books_filter_clicked',
  // A link to /partners (Resources menu, footer, home "Earn with joonoo"); props: source
  // ('menu' | 'mobile-menu' | 'footer' | 'home-earn').
  partners_link_clicked: 'partners_link_clicked',
  // A link to the free family cover (/family-cover); props: source ('menu' | 'mobile-menu' | 'home-hero' | 'gifts-christmas').
  free_cover_link_clicked: 'free_cover_link_clicked',
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

export type CreateFlow = 'picture-book' | 'comic';
export type SuggestionField = 'style' | 'title' | 'idea';

export interface TrackProps {
  flow?: CreateFlow;
  field?: SuggestionField;
  /** Reading-level band (people_confirmed). */
  band?: string;
  /** Elapsed milliseconds (preview_ready). */
  ms?: number;
  style?: string;
  language?: string;
  page_count?: number;
  format?: string;
  source?: string;
  /** Visible text of the clicked button (create_start_clicked). */
  label?: string;
  /** Where the main button sits (create_start_clicked). */
  placement?: 'header' | 'hero' | 'page';
  book_id?: string;
  slug?: string;
  value?: number;
  face_count?: number;
  platform?: string;
  product?: string;
  variant?: string;
  step?: string;
  template?: string;
  reading_level?: string;
  /** Number of copies (extra copies events); cover redraws used so far (cover_redraw). */
  count?: number;
  /** Whether extra copies ride in the main order's box or ship on their own. */
  ships?: 'together' | 'separate';
  /** Fixed failure code (see FAILURE_REASONS), never an error message. */
  reason?: FailureReason;
  /** A CatalogueRecipient id (recipient_tile_clicked). */
  recipient?: string;
  /** A fixed link target id (site nav/outbound events), never a free-text URL. */
  target?: string;
  /** What the parent did with a notice (typed_relative_notice: 'shown' | 'change_photo' | 'continue'). */
  action?: 'shown' | 'change_photo' | 'continue';
  /** Central role ids nobody in the photo can play, comma-joined (central_role_missing), e.g. 'grandparent1'. */
  needs?: string;
  /** Which /books filter a chip belongs to (books_filter_clicked). */
  filter?: 'age' | 'theme' | 'for' | 'type';
}

/** Failure codes for the *_failed / *_rejected events. Codes only: messages can carry personal data. */
export const FAILURE_REASONS = [
  'type', 'size', 'no_faces', 'error', 'http_4xx', 'http_429', 'http_5xx', 'network', 'bad_code', 'title', 'timeout', 'limit',
] as const;
export type FailureReason = (typeof FAILURE_REASONS)[number];

/** Map an HTTP status (or a thrown fetch) to a failure code. */
export function httpFailureReason(status?: number): FailureReason {
  if (!status) return 'network';
  if (status === 429) return 'http_429';
  return status >= 500 ? 'http_5xx' : 'http_4xx';
}

/** Ordered steps of the main purchase funnel (Umami funnel report, 1440-minute window). */
export const MAIN_FUNNEL: EventName[] = [
  'wizard_started',
  'photo_uploaded',
  'faces_detected',
  'faces_confirmed',
  'story_step_viewed',
  'story_generated',
  'cover_preview_requested',
  'preview_viewed',
  'checkout_started',
  'payment_succeeded',
];

/** Ordered steps of the parent-facing create flow (picture-book and comic). */
export const CREATE_FLOW_FUNNEL: EventName[] = [
  'create_step_viewed',
  'photo_uploaded',
  'faces_detected',
  'people_confirmed',
  'preview_requested',
  'preview_ready',
  'checkout_started',
  'payment_succeeded',
];

const WIZARD_TAIL: EventName[] = [
  'photo_uploaded',
  'faces_detected',
  'faces_confirmed',
  'story_step_viewed',
  'story_generated',
  'cover_preview_requested',
  'preview_viewed',
];

/** FAST_PATH_WIZARD A/B: one funnel per arm, each starting at the arm-assignment event. */
export const FAST_PATH_FUNNELS: Record<'control' | 'fast', EventName[]> = {
  control: ['wizard_variant_control', ...WIZARD_TAIL],
  fast: ['wizard_variant_fast', ...WIZARD_TAIL],
};

/** Ordered steps of the free-cover funnel, ending where the main funnel begins. */
export const FREE_COVER_FUNNEL: EventName[] = [
  'free_cover_photo_added',
  'free_cover_theme_chosen',
  'free_cover_code_sent',
  'free_cover_requested',
  'free_cover_ready',
  'free_cover_make_book',
  'wizard_started',
];

export function isEventName(name: string): name is EventName {
  return Object.prototype.hasOwnProperty.call(EVENTS, name);
}
